import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";

type PaymentStatus =
  | "loading"
  | "succeeded"
  | "processing"
  | "requires_payment_method"
  | "error";

interface UseStripePaymentReturn {
  status: PaymentStatus;
  message: string | undefined;
}

export function useStripePayment(): UseStripePaymentReturn {
  const searchParams = useSearchParams();
  const [status, setStatus] = useState<PaymentStatus>("loading");
  const [message, setMessage] = useState<string | undefined>(undefined);
  const hasFetched = useRef(false);

  const stripePublishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
  const stripePromise = stripePublishableKey
    ? loadStripe(stripePublishableKey)
    : null;

  useEffect(() => {
    if (!stripePromise || hasFetched.current) {
      if (!stripePublishableKey) {
        console.error("ERROR: Missing NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY");
        setStatus("error");
        setMessage("Configuration error. Cannot verify payment status.");
      }
      return;
    }

    const clientSecret = searchParams.get("payment_intent_client_secret");

    if (!clientSecret) {
      setStatus("error");
      setMessage("Payment information missing. Could not verify status.");
      hasFetched.current = true;
      return;
    }

    hasFetched.current = true;

    stripePromise.then((stripe) => {
      if (!stripe) {
        setStatus("error");
        setMessage(
          "Could not initialize payment system. Please try again later.",
        );
        return;
      }

      stripe
        .retrievePaymentIntent(clientSecret)
        .then(({ paymentIntent, error }) => {
          if (error) {
            console.error("Error retrieving PaymentIntent:", error);
            setStatus("error");
            setMessage(
              error.message || "An error occurred while verifying payment.",
            );
          } else if (paymentIntent) {
            switch (paymentIntent.status) {
              case "succeeded":
                setStatus("succeeded");
                setMessage(
                  "Thank you for your payment. Your shipment is being processed.",
                );
                break;
              case "processing":
                setStatus("processing");
                setMessage(
                  "Your payment is processing. We'll update you when payment is received.",
                );
                break;
              case "requires_payment_method":
                setStatus("requires_payment_method");
                setMessage(
                  "Payment failed. Please try another payment method.",
                );
                break;
              default:
                setStatus("error");
                setMessage("Something went wrong with the payment.");
                break;
            }
          } else {
            setStatus("error");
            setMessage("Could not retrieve payment details.");
          }
        });
    });
  }, [searchParams, stripePromise, stripePublishableKey]);

  return {
    status,
    message,
  };
}
